import { colorNameMap, GeomapContextProvider, TBP, useCurrentTime } from "@poscon/shared-frontend";
import { Coordinate } from "@poscon/shared-types";
import { WebviewWindow } from "@tauri-apps/api/webviewWindow";
import { LogicalPosition } from "@tauri-apps/api/window";
import { Container, FederatedPointerEvent, Rectangle, Sprite, Texture } from "pixi.js";
import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { EdstButton } from "~/components/utils/EdstButton";
import { useCursorContext } from "~/contexts/cursorContext";
import { gpdStore } from "~/gpdStore";
import { useRootDispatch, useRootSelector } from "~/redux/hooks";
import {
  gpdStateSelector,
  gpdSuppressedSelector,
  setGpdScaleToRange,
  toggleGpdSuppressed,
  translateGpdCenter,
} from "~/redux/slices/gpdSlice";
import { openViewThunk } from "~/redux/thunks/openViewThunk";
import { titleBarHeight } from "../ViewTitleBar";
import { gpdAselSelector, setViewPosition } from "~/redux/slices/edstSlice";
import { edstFontDim } from "~/utils/constants";
import { GeoMap } from "../geo/Geomap";
import {
  allAclFpIdsSelector,
  edstFpSelector,
  forcedConflictsSelector,
} from "~/redux/slices/edstFlightplanSlice";
import { GpdAircraftTrack } from "./GpdAircraftTrack";
import { aircraftSelect } from "~/redux/thunks/aircraftSelect";
import { updateEdstFpData } from "~/redux/thunks/processEramMessage";
import { eramHubConnection } from "~/eramHubConnection";

const y = edstFontDim.height * 2 + 16;

export const GpdBody = () => {
  const dispatch = useRootDispatch();
  const ref = useRef<Container>(null);
  const maskRef = useRef<Sprite>(null);
  const { setShowRecenterCursor } = useCursorContext();
  const gpdState = useRootSelector(gpdStateSelector);
  const asel = useRootSelector(gpdAselSelector);
  const aselEdstFp = useRootSelector((state) => (asel ? edstFpSelector(state, asel.fpId) : null));
  const fpIds = useRootSelector(allAclFpIdsSelector);
  const forcedConflicts = useRootSelector(forcedConflictsSelector);

  const now = useCurrentTime(5000).getTime();

  const gpdStoreValue = useSyncExternalStore(gpdStore.subscribe, gpdStore.getState);
  const { width, height } = gpdStoreValue.rect;
  const [recentering, setRecentering] = useState(false);

  useEffect(() => {
    setShowRecenterCursor(recentering);
    if (window.__TAURI__ && maskRef.current && recentering) {
      const { width, height } = maskRef.current;
      const pos = maskRef.current.getGlobalPosition();
      const newCursorPos = {
        x: pos.x + width / 2,
        y: pos.y + height / 2,
      };
      void WebviewWindow.getCurrent().setCursorPosition(
        new LogicalPosition(newCursorPos.x - 1, newCursorPos.y - 1)
      );
    }
  }, [recentering, setShowRecenterCursor]);

  const suppressed = useRootSelector(gpdSuppressedSelector);

  const handleRangeClick = (event: FederatedPointerEvent) => {
    dispatch(
      setGpdScaleToRange(
        gpdStoreValue.range + (event.button === TBP ? -25 : 25),
        gpdStoreValue.range,
        gpdStoreValue.rect
      )
    );
  };

  const handleSuppressClick = () => {
    dispatch(toggleGpdSuppressed());
  };

  const { geomapId, geomaps } = gpdState.geomapConfig ?? {};

  return (
    <container y={titleBarHeight + 3}>
      <container>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.moveTo(1, y).lineTo(width, y).stroke({ width: 1, color: colorNameMap.grey });
          }}
        />
        <EdstButton
          x={4}
          disabled={asel === null}
          text="Plan Options..."
          onmousedown={(e) => {
            if (asel) {
              const target = e.target as Container;
              const bounds = target.getBounds();
              const newViewPosition = {
                x: bounds.x,
                y: bounds.y + bounds.height,
              };
              dispatch(setViewPosition({ view: "PLAN_OPTIONS", pos: newViewPosition }));
              dispatch(aircraftSelect(asel.fpId, asel.field, "PLAN_OPTIONS"));
            }
          }}
        />
        <EdstButton
          x={4 + edstFontDim.width * 15 + 8 * 2}
          disabled={asel === null}
          text="Hold..."
          onmousedown={(e) => {
            if (asel) {
              const target = e.target as Container;
              const bounds = target.getBounds();
              const newViewPosition = {
                x: bounds.x,
                y: bounds.y + bounds.height,
              };
              dispatch(setViewPosition({ view: "HOLD_MENU", pos: newViewPosition }));
              dispatch(aircraftSelect(asel.fpId, asel.field, "HOLD_MENU"));
            }
          }}
        />
        <EdstButton
          x={4 + edstFontDim.width * 22 + 8 * 4}
          disabled={asel === null}
          text="Show"
          onmousedown={() => {
            if (asel) {
              dispatch(
                updateEdstFpData(asel.fpId, {
                  showConflicts: aselEdstFp?.showConflicts === "sector" ? null : "sector",
                })
              );
            }
          }}
        />
        <EdstButton
          x={4 + edstFontDim.width * 26 + 8 * 6}
          text="Show ALL"
          disabled={asel === null}
          onmousedown={() => {
            if (asel) {
              dispatch(
                updateEdstFpData(asel.fpId, {
                  showConflicts: aselEdstFp?.showConflicts === "all" ? null : "all",
                })
              );
            }
          }}
        />
        <EdstButton x={4 + edstFontDim.width * 34 + 8 * 8} disabled text="Graphic..." />
        <EdstButton
          x={4 + edstFontDim.width * 44 + 8 * 10}
          text="Template..."
          onmousedown={(e) => {
            dispatch(openViewThunk("TEMPLATE_MENU", e.currentTarget as Container));
          }}
        />
        <EdstButton
          x={4 + edstFontDim.width * 55 + 8 * 12}
          text="Clean Up"
          onmousedown={() => eramHubConnection.emit("cleanupGpd")}
        />
      </container>
      <container y={edstFontDim.height + 8}>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics
              .moveTo(0, edstFontDim.height + 8)
              .lineTo(width, edstFontDim.height + 8)
              .stroke({ width: 1, color: 0xadadad });
          }}
        />
        <EdstButton x={4} text="Recenter" onmousedown={() => setRecentering(true)} />
        <EdstButton
          x={4 + edstFontDim.width * 8 + 8 * 2}
          width={edstFontDim.width * 11 + 10}
          onmousedown={handleRangeClick}
          text={`Range ${gpdStoreValue.range}`}
        />
        <EdstButton
          x={4 + edstFontDim.width * 19 + 8 * 4}
          text={!suppressed ? "Suppress" : "Restore"}
          onmousedown={handleSuppressClick}
          width={edstFontDim.width * 8 + 10}
        />
        <EdstButton
          x={4 + edstFontDim.width * 27 + 8 * 6}
          text="Map Options..."
          onmousedown={(e) => {
            dispatch(openViewThunk("GPD_MAP_OPTIONS_MENU", e.currentTarget as Container));
          }}
        />
        <EdstButton
          x={4 + edstFontDim.width * 41 + 8 * 8}
          text="Tools..."
          onmousedown={(e) => {
            dispatch(openViewThunk("TOOLS_MENU", e.currentTarget as Container));
          }}
        />
        <EdstButton x={4 + edstFontDim.width * 49 + 8 * 10} disabled text="Saved Map" />
      </container>
      <sprite y={y + 1} ref={maskRef} texture={Texture.WHITE} width={width - 4} height={height + 2} />
      <container
        hitArea={new Rectangle(0, 0, width, height)}
        mask={maskRef.current}
        y={y}
        eventMode="static"
        label="GPD_CONTAINER"
        ref={ref}
        onMouseDown={(event: FederatedPointerEvent) => {
          if (recentering) {
            const localPos = event.getLocalPosition(ref.current!)!;
            const diff = [
              (localPos.x - gpdStoreValue.rect.width / 2) / gpdState.scale,
              (localPos.y - gpdStoreValue.rect.height / 2) / gpdState.scale,
            ] satisfies Coordinate;
            dispatch(translateGpdCenter(diff));
            setRecentering(false);
          }
        }}
      >
        {recentering && (
          <graphics
            x={Math.floor(width / 2)}
            y={Math.floor(gpdStoreValue.rect.height / 2)}
            draw={(graphics) => {
              graphics.clear();
              graphics
                .moveTo(-11, 0)
                .lineTo(10, 0)
                .moveTo(0, -11)
                .lineTo(0, 10)
                .stroke({ width: 1, color: 0xc0c0c0 });
            }}
          />
        )}
        <container
          isRenderGroup
          sortableChildren
          scale={gpdState.scale}
          x={gpdState.centerOverride[0]}
          y={gpdState.centerOverride[1]}
        >
          <GeomapContextProvider>
            {geomapId &&
              geomaps &&
              Object.entries(geomaps)
                .filter(([id, _]) => gpdState.enabledGeomaps.includes(id))
                .map(([id, geomap]) => (
                  <GeoMap
                    key={id}
                    geomapId={geomapId}
                    filterGroups={geomap.filterGroups}
                    projection={gpdStoreValue.projection}
                    scale={gpdState.scale}
                  />
                ))}
          </GeomapContextProvider>
        </container>
        <container zIndex={2} roundPixels>
          {fpIds.map((fpId) => (
            <GpdAircraftTrack
              key={fpId}
              fpId={fpId}
              geoState={gpdStoreValue}
              state={gpdState}
              forcedConflicts={forcedConflicts}
              now={now}
            />
          ))}
          {[...new Set(Object.values(forcedConflicts).flat())].filter((fpId) => !fpIds.includes(fpId)).map((fpId) => (
            <GpdAircraftTrack
              key={fpId}
              fpId={fpId}
              geoState={gpdStoreValue}
              state={gpdState}
              forcedConflicts={forcedConflicts}
              now={now}
            />
          ))}
        </container>
      </container>
    </container>
  );
};
